import { LookbookSettingsGeneralFormFields, LookbookSettingsLayout } from '@app/components/lookbook/LookbookSettingsPage/components';
import { Form } from '@app/components/various/Form';
import { compiledPaths } from '@app/paths';
import { useLookbookSettingsGeneral } from '@app/services/hooks/lookbook/useLookbookSettingsGeneral';
import { useUpdateLookbookSettingsGeneral } from '@app/services/hooks/lookbook/useUpdateLookbookSettingsGeneral';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { Lookbook } from '@typings/@centra';
import { omit } from 'ramda';
import React from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

const LOOKBOOK_GENERAL_FORM_NAME = 'lookbookSettingsGeneralForm';
export interface LookbookSettingsGeneralPageForm {
  allowAccountRequest?: boolean;
  marketName?: Lookbook.LookbookMarket['name'];
  name?: string;
  ownerId?: Lookbook.LookbookSettingsOwner['id'];
  allowFileDownload?: boolean;
}

export const LookbookSettingsGeneralPage = () => {
  const { lookbookId } = useParams<{ lookbookId: string }>();
  const { data, isLoading: isLoadingLookbookSettingsGeneral } = useLookbookSettingsGeneral({ lookbookId });
  const { mutate: updateLookbookSettingsGeneral, isPending: isPendingUpdateLookbookettingsGeneral } = useUpdateLookbookSettingsGeneral();

  const { allowAccountRequest, market, name, owner } = data ?? {};

  const validationSchema: yup.ObjectSchema<LookbookSettingsGeneralPageForm> = yup.object({
    allowAccountRequest: yup.boolean().required(),
    allowFileDownload: yup.boolean().required(),
    marketName: yup.string().required(),
    name: yup.string(),
    ownerId: yup.number().required(),
  });

  const defaultValues: LookbookSettingsGeneralPageForm = React.useMemo(
    () => ({
      allowAccountRequest,
      allowFileDownload: false,
      marketName: market?.name,
      name,
      ownerId: owner?.id,
    }),
    [allowAccountRequest, market, name, owner],
  );

  const formMethods = useValidatedForm<LookbookSettingsGeneralPageForm>({ defaultValues, validationSchema });

  const handleSubmit = (formData: Lookbook.UpdateLookbookGeneralSettingsDTO) => {
    if (!isDefined(lookbookId)) {
      return;
    }

    updateLookbookSettingsGeneral({
      data: { ...omit(['marketName'], formData) },
      lookbookId,
    });
  };

  const { isDirty } = formMethods.formState;

  React.useEffect(() => {
    if (!isDefined(data)) {
      return;
    }

    formMethods.reset(defaultValues);
  }, [data, defaultValues, formMethods]);

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit} id={LOOKBOOK_GENERAL_FORM_NAME}>
      <LookbookSettingsLayout
        lookbookId={lookbookId}
        formName={LOOKBOOK_GENERAL_FORM_NAME}
        activePath={compiledPaths.LOOKBOOK_SINGLE_SETTINGS_GENERAL({ lookbookId })}
        isDisabled={isLoadingLookbookSettingsGeneral}
        isSubmitting={isPendingUpdateLookbookettingsGeneral}
        hasChanges={isDirty}
        onReset={() => formMethods.reset(defaultValues)}
      >
        <LookbookSettingsGeneralFormFields isLoading={isLoadingLookbookSettingsGeneral} />
      </LookbookSettingsLayout>
    </Form>
  );
};
